import { createSignal } from "solid-js";

export const THEME_STORAGE_KEY = "reliquo-theme";

export type ThemePreference = "light" | "dark" | "system";

export const themeOptions: Array<{
  label: string;
  value: ThemePreference;
  description: string;
}> = [
  { label: "Light", value: "light", description: "Always use the light theme" },
  { label: "Dark", value: "dark", description: "Always use the dark theme" },
  {
    label: "System",
    value: "system",
    description: "Match your device preference",
  },
];

const [theme, setThemeSignal] = createSignal<ThemePreference>("system");

let initialized = false;

export function isThemePreference(
  value: string | null | undefined,
): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function getStoredThemePreference(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function resolveThemeIsDark(
  preference: ThemePreference,
  systemPrefersDark: boolean,
) {
  if (preference === "system") {
    return systemPrefersDark;
  }

  return preference === "dark";
}

export function applyThemePreference(
  preference: ThemePreference,
  systemPrefersDark = false,
) {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.toggle(
    "dark",
    resolveThemeIsDark(preference, systemPrefersDark),
  );
}

export function initializeThemePreference() {
  if (initialized || typeof window === "undefined") {
    return;
  }

  initialized = true;
  const preference = getStoredThemePreference();
  setThemeSignal(preference);
  applyThemePreference(
    preference,
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
}

export function setThemePreference(preference: ThemePreference) {
  setThemeSignal(preference);

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Ignore storage failures and still apply the theme for this session.
    }

    applyThemePreference(
      preference,
      window.matchMedia("(prefers-color-scheme: dark)").matches,
    );
  }

  return preference;
}

export function useTheme() {
  return {
    theme,
    setTheme: setThemePreference,
  };
}

export const THEME_INIT_SCRIPT = `(() => {
	const storageKey = "${THEME_STORAGE_KEY}";
	const isThemePreference = (value) => value === "light" || value === "dark" || value === "system";
	let preference = "system";

	try {
		const stored = window.localStorage.getItem(storageKey);
		if (isThemePreference(stored)) {
			preference = stored;
		}
	} catch {
		preference = "system";
	}

	const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
	const isDark = preference === "dark" || (preference === "system" && prefersDark);
	document.documentElement.classList.toggle("dark", isDark);
})();`;
